import { BufferWriter, Writer } from "protobufjs";
import { PastCaseProject } from "src/domain/model/past_case_project";
import {
  IPastCaseProjectRepository,
  TCreatePastCaseProjectParams,
  TUpdatePastCaseProjectParams,
} from "src/domain/repository/past_case_project_repository";
import { IApiClient } from "../client";
import { api, common, entity } from "../rpc/api";
import { getToken } from "./bind_token";
import Empty = common.Empty;
import PastCaseProjectList = entity.PastCaseProjectList;
import CustomerID = entity.CustomerID;
import PastCaseProjectID = entity.PastCaseProjectID;
import CreatePastCaseProjectRequest = api.CreatePastCaseProjectRequest;
import UpdatePastCaseProjectRequest = api.UpdatePastCaseProjectRequest;

class PastCaseProjectAPI implements IPastCaseProjectRepository {
  constructor(readonly apiClient: IApiClient) {}

  public getAllByCustomer(): Promise<PastCaseProject[]> {
    return new Promise<PastCaseProject[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: Empty = new Empty();
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.PastCaseProjectService/GetAll",
            token,
            Empty.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: PastCaseProjectList = PastCaseProjectList.decode(binary);
          resolve(
            res.items.map((item: entity.PastCaseProject): PastCaseProject => {
              return PastCaseProject.from(item);
            })
          );
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getAllByClient(customerId: string): Promise<PastCaseProject[]> {
    return new Promise<PastCaseProject[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: CustomerID = new CustomerID();
          req.id = customerId;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.PastCaseProjectService/GetAllByCustomer",
            token,
            CustomerID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: PastCaseProjectList = PastCaseProjectList.decode(binary);
          resolve(
            res.items.map((item: entity.PastCaseProject): PastCaseProject => {
              return PastCaseProject.from(item);
            })
          );
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public create(
    createPastCaseProjectRequest: TCreatePastCaseProjectParams
  ): Promise<PastCaseProject> {
    return new Promise<PastCaseProject>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: CreatePastCaseProjectRequest =
            new CreatePastCaseProjectRequest();
          req.title = createPastCaseProjectRequest.title;
          req.meetingNum = createPastCaseProjectRequest.meetingNum;
          req.meetingInterval = createPastCaseProjectRequest.meetingInterval;
          req.meetingMinutes = createPastCaseProjectRequest.meetingMinutes;
          req.workingHour = createPastCaseProjectRequest.workingHour;
          req.workingInterval = createPastCaseProjectRequest.workingInterval;
          req.feePerMonth = createPastCaseProjectRequest.feePerMonth;
          req.workLocation = createPastCaseProjectRequest.workLocation;
          req.workCommunication =
            createPastCaseProjectRequest.workCommunication;
          req.workDetail = createPastCaseProjectRequest.workDetail;
          req.achievementReward =
            createPastCaseProjectRequest.achievementReward;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.PastCaseProjectService/Create",
            token,
            CreatePastCaseProjectRequest.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.PastCaseProject =
            entity.PastCaseProject.decode(binary);
          const item: PastCaseProject = PastCaseProject.from(res);
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public update(
    updatePastCaseProjectRequest: TUpdatePastCaseProjectParams
  ): Promise<PastCaseProject> {
    return new Promise<PastCaseProject>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: UpdatePastCaseProjectRequest =
            new UpdatePastCaseProjectRequest();
          req.id = updatePastCaseProjectRequest.id;
          req.title = updatePastCaseProjectRequest.title;
          req.meetingNum = updatePastCaseProjectRequest.meetingNum;
          req.meetingInterval = updatePastCaseProjectRequest.meetingInterval;
          req.meetingMinutes = updatePastCaseProjectRequest.meetingMinutes;
          req.workingHour = updatePastCaseProjectRequest.workingHour;
          req.workingInterval = updatePastCaseProjectRequest.workingInterval;
          req.feePerMonth = updatePastCaseProjectRequest.feePerMonth;
          req.workLocation = updatePastCaseProjectRequest.workLocation;
          req.workCommunication =
            updatePastCaseProjectRequest.workCommunication;
          req.workDetail = updatePastCaseProjectRequest.workDetail;
          req.achievementReward =
            updatePastCaseProjectRequest.achievementReward;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.PastCaseProjectService/Update",
            token,
            UpdatePastCaseProjectRequest.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.PastCaseProject =
            entity.PastCaseProject.decode(binary);
          const item: PastCaseProject = PastCaseProject.from(res);
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public delete(pastCaseProjectId: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: PastCaseProjectID = new PastCaseProjectID();
          req.id = pastCaseProjectId;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.PastCaseProjectService/Delete",
            token,
            PastCaseProjectID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          resolve();
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }
}

const createPastCaseProjectAPI = (
  apiClient: IApiClient
): IPastCaseProjectRepository => {
  return new PastCaseProjectAPI(apiClient);
};

export { createPastCaseProjectAPI };



// WEBPACK FOOTER //
// ./src/infra/api/service/past_case_project_api.ts